import { ascii, BlockReader, type EmbeddedPicture, type RangeReader } from './reader';
import { readFlac } from './flac';
import { readId3 } from './id3';
import { readMp4 } from './mp4';
import { readOgg } from './ogg';

export type { EmbeddedPicture, RangeReader } from './reader';

/**
 * What an audio file says about itself: its chapter marks, its cover art and,
 * where the container records it, its exact length.
 *
 * The container is recognised from the file's first bytes rather than its
 * name, so a mislabelled file is still read correctly:
 *
 *   ID3      MP3, or FLAC/AAC with an ID3 tag in front   chapters, cover
 *   fLaC     FLAC                                        chapters, cover, length
 *   OggS     Ogg Vorbis, Opus                            chapters, cover
 *   ....ftyp M4B, M4A, MP4                               chapters, cover, length
 *
 * Anything else (WAV, raw AAC, WMA) carries none of these in a standard way,
 * and yields an empty result rather than an error.
 */

export interface MediaChapter {
  title: string;
  /** Offset into this file, in seconds. */
  startSec: number;
}

export interface MediaInfo {
  chapters: MediaChapter[];
  durationSec?: number;
  cover?: EmbeddedPicture;
}

export async function readMediaInfo(read: RangeReader, fileSize: number): Promise<MediaInfo> {
  if (!fileSize) return { chapters: [] };
  const r = new BlockReader(read, fileSize);
  const head = await r.bytes(0, 12);

  if (ascii(head, 0, 3) === 'ID3') {
    const id3 = await readId3(r);
    // FLAC occasionally arrives with an ID3 tag stuck on the front.
    if (ascii(await r.bytes(id3.tagEnd, 4), 0) === 'fLaC') {
      const flac = await readFlac(r, id3.tagEnd);
      return {
        chapters: flac.chapters.length > 0 ? flac.chapters : id3.chapters,
        ...(flac.durationSec ? { durationSec: flac.durationSec } : {}),
        ...((flac.cover ?? id3.cover) ? { cover: flac.cover ?? id3.cover } : {}),
      };
    }
    return { chapters: id3.chapters, ...(id3.cover ? { cover: id3.cover } : {}) };
  }
  if (ascii(head, 0, 4) === 'fLaC') return readFlac(r);
  if (ascii(head, 0, 4) === 'OggS') return readOgg(r);
  if (ascii(head, 4, 4) === 'ftyp') return readMp4(r);
  return { chapters: [] };
}
